/**
 * Turning a wall-clock date and time in a named zone into an exact instant.
 *
 * The date and time somebody types are not an instant. "15 September, 14:30" is a different
 * moment in Manila than in London, and until this file existed the app resolved that
 * ambiguity with whatever timezone the browser happened to be in — so a due date set by
 * someone travelling, or by a colleague in another office, meant something different from
 * what they typed. The zone is now part of the input rather than an accident of the device.
 *
 * No library. `Intl` already knows every zone's offset including its DST history, and a date
 * library here would be several hundred kilobytes to re-answer a question the platform
 * answers correctly.
 */

/** The zone the browser is in. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/**
 * The zone a person chose in their profile menu (saved server-side in `/members/me/preferences`
 * and applied on load by `TimeZonePreferenceSync`). Null means "follow my browser".
 */
let preferredTimeZone: string | null = null

export function setPreferredTimeZone(zone: string | null | undefined): void {
  preferredTimeZone = zone && isValidTimeZone(zone) ? zone : null
}

/**
 * The zone to show and enter times in: the person's saved choice, else the browser's. Every form
 * that defaults its timezone field from here, and every formatter in `lib/dates`, follows it.
 */
export function localTimeZone(): string {
  return preferredTimeZone ?? browserTimeZone()
}

export function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

/**
 * Every zone this browser knows, newest-first browsers only; a short list otherwise.
 *
 * `Intl.supportedValuesOf` is ES2022 and absent in older Safari, so the fallback covers the
 * offices this is actually used from rather than pretending to be exhaustive.
 */
export function availableTimeZones(): string[] {
  try {
    const supported = (
      Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
    ).supportedValuesOf?.('timeZone')
    if (supported?.length) return supported
  } catch {
    // fall through
  }
  return [
    'UTC',
    'Asia/Kolkata',
    'Asia/Manila',
    'Asia/Singapore',
    'Asia/Dubai',
    'Europe/London',
    'Europe/Berlin',
    'America/New_York',
    'America/Chicago',
    'America/Los_Angeles',
    'Australia/Sydney',
  ]
}

/**
 * How far ahead of UTC `zone` is at this instant, in milliseconds.
 *
 * Read back out of `Intl` rather than computed: the offset depends on the date, because of
 * daylight saving and because zones have changed their rules over the years.
 */
function offsetMsAt(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const at: Record<string, number> = {}
  for (const p of parts) if (p.type !== 'literal') at[p.type] = Number(p.value)
  const n = (k: string): number => at[k] ?? 0

  // `hour` comes back as 24 rather than 0 for midnight in some engines under hour12: false.
  const asIfUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour') % 24, n('minute'), n('second'))

  /*
   * Compared against the instant with its MILLISECONDS removed.
   *
   * `asIfUtc` is rebuilt from formatted parts, which stop at seconds. Subtracting a raw
   * `getTime()` therefore folds the instant's milliseconds into the answer, and an offset of
   * +5:30 comes back as +5:29.986 — which is invisible in arithmetic but renders as
   * "GMT+5:29.9859833333" the moment anyone puts it on screen.
   */
  return asIfUtc - (instant.getTime() - instant.getMilliseconds())
}

/**
 * `2026-09-15` + `14:30` + `Asia/Manila` -> the ISO instant that names.
 *
 * Two passes, and the second is not belt-and-braces. The offset has to be looked up at an
 * instant, but the instant is what we are solving for — so the first pass guesses using the
 * offset at the naive time, and the second corrects it using the offset at the guess. Across
 * a daylight-saving boundary the two differ by an hour, and one pass lands an hour out.
 *
 * Returns null for input that is not a complete date and time, so a half-filled form does
 * not produce a confident wrong answer.
 */
export function zonedDateTimeToIso(date: string, time: string, timeZone: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  if (!/^\d{2}:\d{2}(:\d{2})?$/.test(time)) return null

  const [y = 0, m = 1, d = 1] = date.split('-').map(Number)
  const [hh = 0, mm = 0, ss = 0] = time.split(':').map(Number)
  const naive = Date.UTC(y, m - 1, d, hh, mm, ss)

  let ts = naive - offsetMsAt(new Date(naive), timeZone)
  ts = naive - offsetMsAt(new Date(ts), timeZone)

  const out = new Date(ts)
  return Number.isNaN(out.getTime()) ? null : out.toISOString()
}

/** The reverse, for showing a stored instant in a chosen zone's `date` and `time` inputs. */
export function isoToZonedDateTime(
  iso: string,
  timeZone: string,
): { date: string; time: string } | null {
  const instant = new Date(iso)
  if (Number.isNaN(instant.getTime())) return null

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant)

  const at: Record<string, string> = {}
  for (const p of parts) if (p.type !== 'literal') at[p.type] = p.value

  const hour = String(Number(at.hour) % 24).padStart(2, '0')
  return { date: `${at.year}-${at.month}-${at.day}`, time: `${hour}:${at.minute}` }
}

/** `Asia/Manila` -> `Asia / Manila · GMT+8`, for a list somebody has to read. */
export function describeTimeZone(timeZone: string, at: Date = new Date()): string {
  const label = timeZone.replace(/_/g, ' ').replace('/', ' / ')
  try {
    const offset = offsetMsAt(at, timeZone)
    const sign = offset < 0 ? '-' : '+'
    const mins = Math.abs(offset) / 60_000
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return `${label} · GMT${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`
  } catch {
    return label
  }
}

/**
 * Zones that browsers still list under their old name, and what people call them now.
 *
 * Verified rather than assumed: Chrome's `supportedValuesOf` returns `Asia/Calcutta`, not
 * `Asia/Kolkata`. Somebody typing the name their country actually uses finds nothing, and
 * concludes their zone is missing when it is right there under a name changed decades ago.
 *
 * Both directions are listed, because which one a browser returns varies by engine and
 * version — so this has to make either spelling find the entry, whichever way round it is.
 */
const ALSO_KNOWN_AS: Record<string, string> = {
  'Asia/Calcutta': 'Kolkata India',
  'Asia/Kolkata': 'Calcutta India',
  'Asia/Saigon': 'Ho Chi Minh Vietnam',
  'Asia/Ho_Chi_Minh': 'Saigon Vietnam',
  'Asia/Rangoon': 'Yangon Myanmar',
  'Asia/Yangon': 'Rangoon Myanmar',
  'Europe/Kiev': 'Kyiv Ukraine',
  'Europe/Kyiv': 'Kiev Ukraine',
  'America/Godthab': 'Nuuk Greenland',
  'America/Nuuk': 'Godthab Greenland',
  'Asia/Katmandu': 'Kathmandu Nepal',
  'Asia/Kathmandu': 'Katmandu Nepal',
  'Pacific/Enderbury': 'Kanton Kiribati',
  'Asia/Istanbul': 'Turkey',
  'Europe/Istanbul': 'Turkey',
}

/** The other name for a zone, when it has one. Fed to the picker's search text. */
export const timeZoneAlias = (timeZone: string): string | undefined => ALSO_KNOWN_AS[timeZone]

/** Every zone as a picker option, labelled and searchable under either spelling. */
export function timeZoneOptions(): { id: string; label: string; hint?: string }[] {
  const now = new Date()
  return availableTimeZones().map((id) => ({
    id,
    label: describeTimeZone(id, now),
    hint: timeZoneAlias(id),
  }))
}

/** UTC milliseconds of 00:00 on `date` (YYYY-MM-DD) in `timeZone`; DST-correct, same rule as the API. */
export function zonedDayStartMs(date: string, timeZone: string): number {
  const iso = zonedDateTimeToIso(date, '00:00', timeZone)
  return iso ? Date.parse(iso) : Date.parse(`${date}T00:00:00Z`)
}

/** "2026-10-05 10:00" in `timeZone`: readable, and still parses as a date in Excel and Sheets. */
export function formatForSpreadsheet(iso: string, timeZone: string): string {
  const zoned = isoToZonedDateTime(iso, timeZone)
  return zoned ? `${zoned.date} ${zoned.time}` : iso
}

/** Browsers still name some zones by a city's old name; show the one people use today. */
const MODERN_CITY: Record<string, string> = {
  Calcutta: 'Kolkata', Saigon: 'Ho Chi Minh', Rangoon: 'Yangon', Kiev: 'Kyiv', Godthab: 'Nuuk', Katmandu: 'Kathmandu',
}

/** Compact "Kolkata · GMT+5:30" for chips and file headers. */
export function shortTimeZoneLabel(timeZone: string, at: Date = new Date()): string {
  const full = describeTimeZone(timeZone, at)
  const offset = full.split(' · ').pop() ?? ''
  const raw = (timeZone.split('/').pop() ?? timeZone).replace(/_/g, ' ')
  const city = MODERN_CITY[raw] ?? raw
  return offset && offset !== full ? `${city} · ${offset}` : city
}
